import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { supabase } from '@/integrations/supabase/client';

type Language = 'sv' | 'en';

interface Translations {
  [key: string]: {
    sv: string;
    en: string;
  };
}

const translations: Translations = {
  // Navigation
  'nav.home': { sv: 'Hem', en: 'Home' },
  'nav.schedule': { sv: 'Schema', en: 'Schedule' },
  'nav.profile': { sv: 'Min Profil', en: 'My Profile' },
  'nav.academy': { sv: 'Academy', en: 'Academy' },
  'nav.admin': { sv: 'Admin', en: 'Admin' },
  'nav.settings': { sv: 'Inställningar', en: 'Settings' },
  'nav.logout': { sv: 'Logga ut', en: 'Log out' },
  
  // Auth
  'auth.login': { sv: 'Logga in', en: 'Log in' },
  'auth.signup': { sv: 'Registrera dig', en: 'Sign up' },
  'auth.email': { sv: 'E-post', en: 'Email' },
  'auth.password': { sv: 'Lösenord', en: 'Password' },
  'auth.fullName': { sv: 'Fullständigt namn', en: 'Full name' },
  'auth.loginWithGoogle': { sv: 'Logga in med Google', en: 'Log in with Google' },
  'auth.noAccount': { sv: 'Har du inget konto?', en: "Don't have an account?" },
  'auth.hasAccount': { sv: 'Har du redan ett konto?', en: 'Already have an account?' },
  'auth.welcomeBack': { sv: 'Välkommen tillbaka', en: 'Welcome back' },
  'auth.createAccount': { sv: 'Skapa konto', en: 'Create account' },
  'auth.joinUs': { sv: 'Gå med i ElitPlayer', en: 'Join ElitPlayer' },
  
  // Dashboard
  'dashboard.welcome': { sv: 'Välkommen tillbaka', en: 'Welcome back' },
  'dashboard.nextSession': { sv: 'Nästa träningspass', en: 'Next training session' },
  'dashboard.noUpcoming': { sv: 'Inga kommande pass', en: 'No upcoming sessions' },
  'dashboard.myMissions': { sv: 'Mina Uppdrag', en: 'My Missions' },
  'dashboard.noMissions': { sv: 'Inga tilldelade uppdrag', en: 'No assigned missions' },
  'dashboard.reportComplete': { sv: 'Rapportera Slutfört', en: 'Report Complete' },
  'dashboard.deadline': { sv: 'Deadline', en: 'Deadline' },
  'dashboard.markAttending': { sv: 'Anmäl Närvaro', en: 'Mark Attending' },
  'dashboard.markAbsent': { sv: 'Anmäl Frånvaro', en: 'Mark Absent' },
  'dashboard.attending': { sv: 'Närvarande', en: 'Attending' },
  'dashboard.absent': { sv: 'Frånvarande', en: 'Absent' },
  
  // Report Modal
  'report.title': { sv: 'Rapportera Slutfört', en: 'Report Complete' },
  'report.howDidItFeel': { sv: 'Hur kändes passet?', en: 'How did the session feel?' },
  'report.rpe': { sv: 'Ansträngningsnivå (RPE)', en: 'Effort Level (RPE)' },
  'report.comment': { sv: 'Kommentar till coach', en: 'Comment to coach' },
  'report.commentPlaceholder': { sv: 'Beskriv hur träningen gick...', en: 'Describe how the training went...' },
  'report.submit': { sv: 'Skicka Rapport', en: 'Submit Report' },
  'report.success': { sv: 'Rapport skickad!', en: 'Report submitted!' },
  
  // Profile
  'profile.title': { sv: 'Min Profil', en: 'My Profile' },
  'profile.attributes': { sv: 'Spelarattribut', en: 'Player Attributes' },
  'profile.discipline': { sv: 'Träningsdisciplin', en: 'Training Discipline' },
  'profile.attendance': { sv: 'Närvaro', en: 'Attendance' },
  'profile.completedMissions': { sv: 'Slutförda Uppdrag', en: 'Completed Missions' },
  'profile.developmentLog': { sv: 'Min Utvecklingslogg', en: 'My Development Log' },
  'profile.noFeedback': { sv: 'Ingen feedback ännu', en: 'No feedback yet' },
  'profile.technique': { sv: 'Teknik', en: 'Technique' },
  'profile.physique': { sv: 'Fysik', en: 'Physique' },
  'profile.gameUnderstanding': { sv: 'Spelförståelse', en: 'Game Understanding' },
  'profile.shooting': { sv: 'Skott', en: 'Shooting' },
  'profile.passing': { sv: 'Passning', en: 'Passing' },
  'profile.speed': { sv: 'Snabbhet', en: 'Speed' },
  
  // Academy
  'academy.title': { sv: 'Academy', en: 'Academy' },
  'academy.nutrition': { sv: 'Kostråd', en: 'Nutrition' },
  'academy.videoAnalysis': { sv: 'Videoanalys', en: 'Video Analysis' },
  'academy.trainingVideos': { sv: 'Träningsfilmer', en: 'Training Videos' },
  'academy.articles': { sv: 'Artiklar', en: 'Articles' },
  'academy.noContent': { sv: 'Inget innehåll tillgängligt', en: 'No content available' },
  
  // Admin
  'admin.title': { sv: 'Tränar-Dashboard', en: 'Coach Dashboard' },
  'admin.playerOverview': { sv: 'Spelaröverblick', en: 'Player Overview' },
  'admin.incomingReports': { sv: 'Inkommande Rapporter', en: 'Incoming Reports' },
  'admin.createMission': { sv: 'Skapa Uppdrag', en: 'Create Mission' },
  'admin.registerPayment': { sv: 'Registrera Betalning', en: 'Register Payment' },
  'admin.updateProfile': { sv: 'Uppdatera Spelarprofil', en: 'Update Player Profile' },
  'admin.inviteUser': { sv: 'Bjud in Användare', en: 'Invite User' },
  'admin.disciplineScore': { sv: 'Discipline Score', en: 'Discipline Score' },
  'admin.paymentStatus': { sv: 'Betalningsstatus', en: 'Payment Status' },
  'admin.agencyStatus': { sv: 'Agenturstatus', en: 'Agency Status' },
  'admin.paid': { sv: 'Betald', en: 'Paid' },
  'admin.unpaid': { sv: 'Ej betald', en: 'Unpaid' },
  'admin.payingCustomer': { sv: 'Betalande Kund', en: 'Paying Customer' },
  'admin.highPotential': { sv: 'High Potential', en: 'High Potential' },
  'admin.agencyProspect': { sv: 'Agency Prospect', en: 'Agency Prospect' },
  'admin.filterBy': { sv: 'Filtrera efter', en: 'Filter by' },
  'admin.sortBy': { sv: 'Sortera efter', en: 'Sort by' },
  'admin.allPlayers': { sv: 'Alla spelare', en: 'All players' },
  
  // Common
  'common.loading': { sv: 'Laddar...', en: 'Loading...' },
  'common.save': { sv: 'Spara', en: 'Save' },
  'common.cancel': { sv: 'Avbryt', en: 'Cancel' },
  'common.delete': { sv: 'Ta bort', en: 'Delete' },
  'common.edit': { sv: 'Redigera', en: 'Edit' },
  'common.send': { sv: 'Skicka', en: 'Send' },
  'common.reply': { sv: 'Svara', en: 'Reply' },
  'common.player': { sv: 'Spelare', en: 'Player' },
  'common.trainer': { sv: 'Tränare', en: 'Trainer' },
  'common.admin': { sv: 'Admin', en: 'Admin' },
};

interface LanguageContextType {
  language: Language;
  setLanguage: (lang: Language) => void;
  t: (key: string) => string;
}

const LanguageContext = createContext<LanguageContextType | undefined>(undefined);

export function LanguageProvider({ children }: { children: ReactNode }) {
  const [language, setLanguage] = useState<Language>('sv');

  // Load from localStorage on mount
  useEffect(() => {
    const saved = localStorage.getItem('elitplayer-language') as Language;
    if (saved && (saved === 'sv' || saved === 'en')) {
      setLanguage(saved);
    }
  }, []);

  // Sync language from database when user logs in
  useEffect(() => {
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      (event, session) => {
        if ((event === 'SIGNED_IN' || event === 'INITIAL_SESSION') && session?.user) {
          // Fetch preferred_language from profiles
          supabase
            .from('profiles')
            .select('preferred_language')
            .eq('id', session.user.id)
            .single()
            .then(({ data }) => {
              if (data?.preferred_language && (data.preferred_language === 'sv' || data.preferred_language === 'en')) {
                setLanguage(data.preferred_language as Language);
                localStorage.setItem('elitplayer-language', data.preferred_language);
              }
            });
        }
      }
    );

    return () => subscription.unsubscribe();
  }, []);

  const handleSetLanguage = async (lang: Language) => {
    setLanguage(lang);
    localStorage.setItem('elitplayer-language', lang);

    // Sync to database if user is logged in
    const { data: { session } } = await supabase.auth.getSession();
    if (session?.user) {
      supabase
        .from('profiles')
        .update({ preferred_language: lang })
        .eq('id', session.user.id)
        .then(({ error }) => {
          if (error) console.error('Failed to sync language to database:', error);
        });
    }
  };

  const t = (key: string): string => {
    const translation = translations[key];
    if (!translation) {
      console.warn(`Missing translation for key: ${key}`);
      return key;
    }
    return translation[language];
  };

  return (
    <LanguageContext.Provider value={{ language, setLanguage: handleSetLanguage, t }}>
      {children}
    </LanguageContext.Provider>
  );
}

export function useLanguage() {
  const context = useContext(LanguageContext);
  if (context === undefined) {
    throw new Error('useLanguage must be used within a LanguageProvider');
  }
  return context;
}
